'use client'

import { useSearchParams } from 'next/navigation'
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'
import type ReCAPTCHA from 'react-google-recaptcha'

import { AlertBox } from '@/components/common/AlertBox'
import { ButtonDefault } from '@/components/common/Button'
import { InputText } from '@/components/common/Input'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { useReduxSelector } from '@/store/hooks'
import { trackEvent } from '@/utils/analyticsUtil'
import { cn } from '@/utils/commonUtil'
import { createDynamicComponent } from '@/utils/factoryUtil'
import { fetchApi } from '@/utils/requestUtil'

import styles from './FormNewsletter.module.scss'

const Captcha = createDynamicComponent({
  loader: () => import('@/components/common/Captcha'),
  loading: false,
  name: 'Captcha',
})

const SUBMIT_ERROR = 'Something went wrong. Please try again.'

type Props = {
  enableAutoSubmit?: true
}

export const FormNewsletter = ({ enableAutoSubmit }: Props) => {
  const userEmail = useReduxSelector((s) => s.user.profile?.email)
  const searchParams = useSearchParams()
  const emailParam = searchParams.get('email')?.replace(/\s/g, '+')
  const subscribeParam = searchParams.get('subscribe')
  const [email, setEmail] = useState(emailParam || userEmail || '')
  const hasAutoSubscribed = useRef(false)
  const [error, setError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState<ReCAPTCHA | null>(null)
  const [showCaptcha, setShowCaptcha] = useState(!!(enableAutoSubmit && subscribeParam && emailParam))
  const pendingSubmit = useRef(false)

  const submitSubscription = useCallback(() => {
    setError('')
    setLoading(true)

    void (async () => {
      try {
        captcha?.reset()
        const token = await captcha?.executeAsync()

        if (!token) {
          setError('Please complete the captcha.')
          return
        }

        await fetchApi({
          base: 'api',
          options: {
            body: JSON.stringify({ email, token }),
            headers: { accept: 'application/json', 'content-type': 'application/json' },
            method: 'POST',
          },
          path: '/newsletter',
        })
        setIsSuccess(true)
        trackEvent({ action: 'newsletter_subscribe', category: 'newsletter' })
      } catch (e) {
        console.error(e)
        setError(SUBMIT_ERROR)
      } finally {
        setLoading(false)
      }
    })()
  }, [captcha, email])

  const onSubmit = useCallback(
    (e?: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      e?.preventDefault()
      setShowCaptcha(true)

      if (!captcha) {
        pendingSubmit.current = true
        setError('')
        setLoading(true)
        return
      }

      submitSubscription()
    },
    [captcha, submitSubscription],
  )

  useEffect(() => {
    if (!captcha || !pendingSubmit.current) return
    pendingSubmit.current = false
    submitSubscription()
  }, [captcha, submitSubscription])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (userEmail && !emailParam) setEmail(userEmail)
  }, [userEmail, emailParam])

  useEffect(() => {
    if (!enableAutoSubmit || !subscribeParam || !emailParam || !captcha || hasAutoSubscribed.current) return
    hasAutoSubscribed.current = true
    submitSubscription()
  }, [captcha, emailParam, subscribeParam, submitSubscription, enableAutoSubmit])

  if (isSuccess) {
    return (
      <p className={styles.message}>
        <IconWithLabel iconBefore='check' iconBeforeSize='sm'>{`You're subscribed!`}</IconWithLabel>
      </p>
    )
  }

  return (
    <div className={cn(styles.container, 'embedded')}>
      {error && (
        <AlertBox>
          <p>
            <strong>Error:</strong> {error}
          </p>
        </AlertBox>
      )}
      <form onSubmit={onSubmit}>
        {showCaptcha && <Captcha ref={setCaptcha} size='invisible' />}
        <div className={styles.fields}>
          <div className={styles.email}>
            <InputText
              id='email'
              label='Email'
              onChange={setEmail}
              onFocus={() => setShowCaptcha(true)}
              required
              type='email'
              value={email}
            />
          </div>
          <div className={styles.submit}>
            <ButtonDefault colorScheme='dark' disabled={loading || !email} loading={loading} size='lg' type='submit'>
              Subscribe
            </ButtonDefault>
          </div>
        </div>
      </form>
    </div>
  )
}
